import { Injectable } from '@angular/core';
import { Web3 } from 'web3';
import { ethers } from 'ethers';
import IrisP2PABI from './abi/IrisP2PABI.json';
import { Web3Service } from './web3Service';
import { BASE_URL } from './api.url';
import { withBscRpcFallback } from './eth-rpc-fallback.util';

export enum TradeStatus { Open = 0, Paid = 1, Completed = 2, Cancelled = 3, Disputed = 4 }
export enum OrderStatus { Active = 0, Completed = 1, Cancelled = 2 }

export interface P2POrder {
  id: string;
  seller: string;
  totalAmount: string; // raw wei
  remaining: string;   // raw wei
  minBuy: string;      // raw wei
  maxBuy: string;      // raw wei
  rate: string;        // raw rate units (×1e8) — use fromRateUnits() to display
  currency: string;
  paymentMethods: string[];
  sellerTelegram?: string;
  queueLength: string;
  paymentDelay: string; // seconds
  status: OrderStatus;
}

export interface P2PTrade {
  id: string;
  orderId: string;
  buyer: string;
  amount: string;      // raw wei
  expiresAt: number;   // ms timestamp
  paidAt: number;      // ms timestamp
  status: TradeStatus;
  isExpired: boolean;
  canReclaim: boolean;
  canDispute: boolean;
  sellerAddress?: string;
  paymentMethods?: string[];
  sellerTelegram?: string;
  currency?: string;
  rate?: string;       // raw rate units (×1e8)
}

export interface SellOrderParams {
  amountRaw: string;        // wei string
  minBuyRaw: string;        // wei string
  maxBuyRaw: string;        // wei string — '0' = no max
  rateUnits: string;        // rate * 1e8 string
  currency: string;         // 3-char code
  paymentMethods: string[]; // string identifiers
  expirationSeconds: string; // seconds
}

export interface P2PReputation {
  total: string;
  success: string;
  cancellations: string;
  banned: boolean;
  activeCount: string;
  rewards: string;
  referralRewards: string;
  telegramUsername: string;
}

export interface P2PLimits {
  maxSellable: string; // wei
  minSellable: string; // wei
  minFlatFee: string;  // wei
  feePercent: string;  // BPS (100 = 1%)
  rewardSplit: string; // BPS
}

export interface JudgeInfo {
  active: boolean;
  balance: string;       // USDT rewards en wei
  totalResolved: string;
}

export interface JudgeRecord extends JudgeInfo {
  address: string;
  telegramUsername: string;
}

// Un litige, en cours OU passé (2026-09-13, demandé par l'utilisateur : "ajouter la liste des
// litiges en cours et litiges passés" sur /p2p/p2p-admin) — voir getDisputeHistory().
export interface DisputeRecord {
  tradeId: string;
  initiator: string;
  disputedAt: number;      // ms timestamp (bloc TradeDisputed)
  resolved: boolean;
  judge?: string;
  buyerWins?: boolean;
  resolvedAt?: number;     // ms timestamp (bloc TradeResolved)
  trade: P2PTrade | null;  // enrichi via getTrade() — null si le trade n'a pas pu être relu
}

export interface AdminOverview {
  owner: string;
  paused: boolean;
  pausedDeposit: boolean;
  pausedRewards: boolean;
  pausedTaxes: boolean;
  totalOrders: string;
  totalTrades: string;
  totalSuccessfulTrades: string;
  totalEscrowed: string;    // wei
  accumulatedFees: string;  // wei
  judgeCount: string;
  contractBalance: string;  // wei
  isBalanceHealthy: boolean;
  maxSellableAmount: string; // wei
  minSellableAmount: string; // wei
  minFlatFee: string;        // wei
  maxActiveTrades: string;
  defaultExpirationTime: string; // seconds
  feePercent: string;   // BPS
  rewardSplit: string;  // BPS
  judgeTaxPct: string;  // BPS
}

// ── Réseau BSC ───────────────────────────────────────────────────────────────
// Passer USE_TESTNET à false pour passer en production BSC mainnet
const USE_TESTNET = false;

const BSC_RPC = USE_TESTNET
  ? 'https://data-seed-prebsc-1-s1.binance.org:8545/'
  : 'https://bsc-dataseed.binance.org/';

// BUG CORRIGÉ (2026-09-13, signalé par l'utilisateur : liste des litiges passés vide alors que
// des litiges existent) — bsc-dataseed.binance.org (BSC_RPC ci-dessus, utilisé pour TOUT le
// reste : lectures d'état, préparation des écritures) rejette eth_getLogs INCONDITIONNELLEMENT,
// même sur 1 seul bloc ("limit exceeded", vérifié empiriquement) — ce n'est pas une histoire de
// plage trop large, cette méthode y est purement désactivée. Endpoint dédié UNIQUEMENT pour les
// requêtes d'événements passés (getPastEvents), jamais pour le reste — aucun fournisseur gratuit
// testé (PublicNode, Ankr, NodeReal, BscScan/Etherscan v2) ne couvre l'historique complet du
// contrat sans clé payante ; PublicNode accepte ~9000 blocs en arrière avant d'exiger un abonnement
// "archive" (≈8h à 3s/bloc) — fenêtre glissante acceptée sciemment par l'utilisateur plutôt que de
// payer un service tiers, voir EVENT_LOOKBACK_BLOCKS.
const EVENTS_RPC = 'https://bsc.publicnode.com';
const EVENT_LOOKBACK_BLOCKS = 8000; // marge sous les ~9000 blocs qui basculent en "archive requis"

const EXCHANGE_ADDRESS = USE_TESTNET
  ? '0x11211CE1d74b9ea9521C9b3914100eC1A4581941'  // BSC Testnet
  : '0xc2100E04650996634008c3dA74Bb3e00874d4158';  // BSC Mainnet
const USDT_ADDRESS     = USE_TESTNET
  ? '0x71f951f2a192379b108aF56c637bFE2dEc0db4CD'  // BSC Testnet
  : '0x55d398326f99059fF775485246999027B3197955'; // BSC Mainnet
const TOKEN_DECIMALS   = 18;

const USDT_ABI = [
  { inputs: [{ name: 'account', type: 'address' }], name: 'balanceOf', outputs: [{ name: '', type: 'uint256' }], stateMutability: 'view', type: 'function' },
  { inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }], name: 'approve', outputs: [{ name: '', type: 'bool' }], stateMutability: 'nonpayable', type: 'function' },
  { inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }], name: 'allowance', outputs: [{ name: '', type: 'uint256' }], stateMutability: 'view', type: 'function' },
] as const;

@Injectable({ providedIn: 'root' })
export class IrisP2PService {
  private _web3 = new Web3(BSC_RPC);
  private _exchange: any;
  private _usdt: any;
  // Voir EVENTS_RPC — instance séparée UNIQUEMENT pour getPastEvents (BSC_RPC les rejette tous).
  private _eventsWeb3 = new Web3(EVENTS_RPC);
  private _eventsExchange: any;

  constructor(private _web3Svc: Web3Service) {
    this._exchange = new this._web3.eth.Contract(IrisP2PABI as any, EXCHANGE_ADDRESS);
    this._usdt     = new this._web3.eth.Contract(USDT_ABI as any, USDT_ADDRESS);
    this._eventsExchange = new this._eventsWeb3.eth.Contract(IrisP2PABI as any, EXCHANGE_ADDRESS);
  }

  // RPC direct (pas le provider du wallet) pour la confirmation — Trust Wallet via WC rejette
  // eth_blockNumber. Repli sur 3 endpoints publics avec timeout court par tentative (voir
  // eth-rpc-fallback.util.ts) plutôt qu'un unique endpoint sans secours et sans timeout explicite
  // (300s par défaut chez ethers) : un hoquet sur le 1er endpoint bloquait toute confirmation de
  // transaction (approve/setTelegram/placeSellOrder) au lieu de basculer sur un autre endpoint.
  private async _directWait(tx: any): Promise<any> {
    const hash = tx?.hash;
    if (!/^0x([0-9a-fA-F]{64})$/.test(hash ?? '')) {
      throw new Error('Transaction envoyée, mais hash invalide ou absent.');
    }
    const receipt = await withBscRpcFallback((p) => p.waitForTransaction(hash, 1, 40_000));
    if (!receipt) throw new Error(`Transaction non confirmée: ${hash}`);
    return receipt;
  }

  // BUG CORRIGÉ (2026-08-24) : approveUsdt/setTelegramUsername/placeSellOrder/setReferrerOnChain
  // faisaient `const tx = await contract[...](...)` — un `await` DIRECT sur la demande de
  // signature WalletConnect elle-même (pas sur _directWait, déjà borné à 40s lui). Ce premier await
  // n'a AUCUN filet : si le relay WC ne délivre jamais sa réponse (fréquent en Mini App Telegram,
  // notamment quand l'app wallet repasse au premier plan), ethers finit par rejeter avec
  // "wc_sign_timeout" tel quel côté UI (voir errorMessage() plus bas, qui n'avait pas non plus de
  // garde anti-JSON-brut) — c'est très probablement LA raison pour laquelle /p2p/p2p-sell échouait
  // presque systématiquement en Mini App Telegram et jamais sur app.iristoken.io (page web classique,
  // moins exposée aux hoquets de relay liés au premier plan/arrière-plan de l'app wallet).
  //
  // Même pattern fire-and-forget + poll qu'ailleurs dans l'app (earn-portfolio.page.ts,
  // buy-iris.page.ts...) : on envoie sans attendre la réponse WC, et on vérifie l'état on-chain
  // RÉEL en parallèle via RPC direct (jamais tributaire du relay).
  private async _pollUntil(check: () => Promise<boolean>, getErr: () => any, timeoutMsg: string, timeoutMs = 120_000, intervalMs = 4_000): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, intervalMs));
      const e = getErr();
      if (e) throw e;
      if (await this.pollTimeout(check(), 15_000).catch(() => false)) return;
    }
    throw new Error(timeoutMsg);
  }

  // Toutes les lectures de ce service (`.methods.xxx().call()`) passent par `this._web3`, un
  // client Web3.js lié à un unique endpoint BSC sans repli — et son HttpProvider fait un `fetch()`
  // brut SANS AUCUN timeout (vérifié dans web3-providers-http: aucun AbortController/signal par
  // défaut). Si cet endpoint hérite d'un accroc pendant qu'on lit un statut en boucle (ex: après
  // avoir annulé un trade, en attendant que le nouveau statut apparaisse), la lecture reste
  // bloquée indéfiniment — et comme cet `await` est À L'INTÉRIEUR d'une boucle `while(deadline)`,
  // le deadline ne peut jamais être revérifié tant que cette lecture n'a pas abouti. À utiliser
  // pour border chaque lecture faite depuis une boucle de polling (voir _pollUntil* des pages P2P).
  pollTimeout<T>(p: Promise<T>, ms = 15_000): Promise<T> {
    return Promise.race([
      p,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error('p2p_read_timeout')), ms)),
    ]);
  }

  // ── Balance ────────────────────────────────────────────────────────────────

  async getUsdtBalance(address: string): Promise<string> {
    try {
      const raw = await this._usdt.methods.balanceOf(address).call();
      return String(raw);
    } catch { return '0'; }
  }

  // ── Reputation ────────────────────────────────────────────────────────────

  async getReputation(address: string): Promise<P2PReputation | null> {
    try {
      const r: any   = await this._exchange.methods.getAccount(address).call();
      const rw: any  = await this._exchange.methods.getUserRewards(address).call().catch(() => null);
      const success  = String(r.successfulTrades ?? r[3] ?? '0');
      const cancels  = String(r.cancellations    ?? r[4] ?? '0');
      return {
        total:           (BigInt(success) + BigInt(cancels)).toString(),
        success,
        cancellations:   cancels,
        banned:          Boolean(r.banned ?? r[1]),
        activeCount:     await this.getActiveTrades(address),
        rewards:         String(rw?.userRewards         ?? rw?.[0] ?? '0'),
        referralRewards: String(rw?.userReferralRewards ?? rw?.[1] ?? '0'),
        telegramUsername: this.fromBytes32(String(r.telegramUsername ?? r[2] ?? '')),
      };
    } catch { return null; }
  }

  async getActiveTrades(address: string): Promise<string> {
    try { return String(await this._exchange.methods.activeTrades(address).call()); }
    catch { return '0'; }
  }

  async getMaxActiveTrades(): Promise<string> {
    try { return String(await this._exchange.methods.maxActiveTrades().call()); }
    catch { return '3'; }
  }

  async getTelegramUsername(address: string): Promise<string> {
    try {
      const raw = await this._exchange.methods.getTelegramUsername(address).call();
      return this.fromBytes32(String(raw));
    } catch { return ''; }
  }

  // ── Contract limits ────────────────────────────────────────────────────────

  async getLimits(): Promise<P2PLimits> {
    try {
      const [maxSellable, minSellable, minFlatFee, feePercent, rewardSplit] = await Promise.all([
        this._exchange.methods.maxSellableAmount().call(),
        this._exchange.methods.minSellableAmount().call(),
        this._exchange.methods.minFlatFee().call(),
        this._exchange.methods.feePercent().call(),
        this._exchange.methods.rewardSplit().call(),
      ]);
      return {
        maxSellable: String(maxSellable),
        minSellable: String(minSellable),
        minFlatFee:  String(minFlatFee),
        feePercent:  String(feePercent),
        rewardSplit: String(rewardSplit),
      };
    } catch {
      return { maxSellable: '0', minSellable: '0', minFlatFee: '0', feePercent: '100', rewardSplit: '5000' };
    }
  }

  // ── Orders ────────────────────────────────────────────────────────────────

  async getOrder(id: string): Promise<P2POrder | null> {
    try {
      const o: any = await this._exchange.methods.getOrder(id).call();
      const methods: any[] = await this._exchange.methods.getOrderPaymentMethods(id).call().catch(() => []);
      const sellerTgRaw    = await this._exchange.methods.getSellerTelegram(id).call().catch(() => '');

      return {
        id,
        seller:       String(o.seller       ?? o[1]  ?? ''),
        totalAmount:  String(o.amount       ?? o[2]  ?? '0'),
        remaining:    String(o.availableAmount ?? o[3] ?? o.remaining ?? '0'),
        minBuy:       String(o.minBuyableAmount ?? o[4] ?? '0'),
        maxBuy:       String(o.maxBuyableAmount ?? o[5] ?? '0'),
        rate:         String(o.rate         ?? o[6]  ?? '0'),
        currency:     this.fromBytes3(String(o.currency  ?? o[7]  ?? '0x000000')),
        queueLength:  String(o.transactionInProgress ?? o[10] ?? '0'),
        paymentDelay: String(o.expirationTime ?? o[9]  ?? '3600'),
        status:       Number(o.status       ?? o[12] ?? 0) as OrderStatus,
        paymentMethods: methods.map(m => this.fromBytes32(String(m))),
        sellerTelegram: this.fromBytes32(String(sellerTgRaw)),
      };
    } catch { return null; }
  }

  async getActiveOrders(limit = 40): Promise<P2POrder[]> {
    try {
      // Try paginated endpoint first (most efficient)
      const ids: any[] = await this._exchange.methods.getActiveOrderIds().call();
      const slice = ids.slice(-limit).reverse();
      const results = await Promise.allSettled(slice.map(id => this.getOrder(String(id))));
      return results
        .filter((r): r is PromiseFulfilledResult<P2POrder> => r.status === 'fulfilled' && !!r.value)
        .map(r => r.value)
        .filter(o => o.status === OrderStatus.Active && BigInt(o.remaining) >= BigInt(o.minBuy));
    } catch {
      // Fallback: iterate by count
      const count = Number(await this._exchange.methods.totalOrders().call().catch(() => 0));
      const start  = Math.max(1, count - limit + 1);
      const promises = [];
      for (let i = count; i >= start; i--) promises.push(this.getOrder(String(i)));
      const results = await Promise.allSettled(promises);
      return results
        .filter((r): r is PromiseFulfilledResult<P2POrder> => r.status === 'fulfilled' && !!r.value)
        .map(r => r.value)
        .filter(o => o.status === OrderStatus.Active && BigInt(o.remaining) >= BigInt(o.minBuy));
    }
  }

  async getUserOrders(address: string): Promise<string[]> {
    try { return (await this._exchange.methods.getUserOrders(address).call()).map(String); }
    catch { return []; }
  }

  // ── Trades ────────────────────────────────────────────────────────────────

  async getTrade(tradeId: string): Promise<P2PTrade | null> {
    try {
      const t: any = await this._exchange.methods.trades(tradeId).call();
      const orderId = String(t.orderId ?? t[0] ?? '0');
      const status  = Number(t.status  ?? t[7] ?? 0) as TradeStatus;
      const expSec  = Number(t.expirationDate ?? t[4] ?? 0);
      const nowSec  = Math.floor(Date.now() / 1000);
      const isExpired   = expSec > 0 && nowSec > expSec && status === TradeStatus.Open;
      const canReclaim  = isExpired;
      const disputeDeadline = Number(t.disputeDeadline ?? t[6] ?? 0);
      const canDispute  = status === TradeStatus.Paid && nowSec <= disputeDeadline;

      let sellerAddress = '', paymentMethods: string[] = [], sellerTelegram = '', currency = '', rate = '';
      try {
        const order = await this.getOrder(orderId);
        if (order) {
          sellerAddress  = order.seller;
          paymentMethods = order.paymentMethods;
          sellerTelegram = order.sellerTelegram ?? '';
          currency       = order.currency;
          rate           = order.rate;
        }
      } catch {}

      return {
        id: tradeId,
        orderId,
        buyer:        String(t.buyer  ?? t[2] ?? ''),
        amount:       String(t.amount ?? t[3] ?? '0'),
        expiresAt:    expSec * 1000,
        paidAt:       Number(t.paidAt ?? t[5] ?? 0) * 1000,
        status,
        isExpired,
        canReclaim,
        canDispute,
        sellerAddress,
        paymentMethods,
        sellerTelegram,
        currency,
        rate,
      };
    } catch { return null; }
  }

  async getUserTrades(address: string): Promise<string[]> {
    try { return (await this._exchange.methods.getUserTrades(address).call()).map(String); }
    catch { return []; }
  }

  async getOrderTradeIds(orderId: string): Promise<string[]> {
    try { return (await this._exchange.methods.getOrderTradeIds(orderId).call()).map(String); }
    catch { return []; }
  }

  // ── Write helpers ─────────────────────────────────────────────────────────

  private writeExchange(signer: any): ethers.Contract {
    return new ethers.Contract(EXCHANGE_ADDRESS, IrisP2PABI as any, signer);
  }

  private writeUsdt(signer: any): ethers.Contract {
    return new ethers.Contract(USDT_ADDRESS, [
      'function approve(address spender, uint256 amount) returns (bool)',
    ], signer);
  }

  // ── Write — Sell flow ─────────────────────────────────────────────────────

  async approveUsdt(signer: any, amountRaw: string): Promise<void> {
    const usdt   = this.writeUsdt(signer);
    const owner  = await signer.getAddress();
    const target = BigInt(amountRaw);

    let wcErr: any;
    usdt['approve'](EXCHANGE_ADDRESS, target)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => BigInt(await this._usdt.methods.allowance(owner, EXCHANGE_ADDRESS).call()) >= target,
      () => wcErr,
      'Délai dépassé — approbation USDT non confirmée à temps.',
    );
  }

  async setTelegramUsername(signer: any, username: string): Promise<void> {
    const contract = this.writeExchange(signer);
    const clean    = username.replace(/^@/, '').trim();
    const bytes32  = this.strToBytes32(clean);
    const owner    = await signer.getAddress();

    let wcErr: any;
    contract['setTelegramUsername'](bytes32)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getTelegramUsername(owner)) === clean,
      () => wcErr,
      "Délai dépassé — enregistrement du username Telegram non confirmé à temps.",
    );
  }

  async placeSellOrder(signer: any, params: SellOrderParams): Promise<string> {
    const currBytes3 = this.strToBytes3(params.currency);
    const methods    = params.paymentMethods.map(m => this.strToBytes32(m));

    const orderParams = {
      amount:            BigInt(params.amountRaw),
      minBuyableAmount:  BigInt(params.minBuyRaw),
      maxBuyableAmount:  BigInt(params.maxBuyRaw),
      rate:              BigInt(params.rateUnits),
      currency:          currBytes3,
      paymentMethods:    methods,
      expirationTime:    BigInt(params.expirationSeconds),
    };

    const contract = this.writeExchange(signer);
    // staticCall = simulation en lecture seule (eth_call), pas une demande de signature — pas le
    // même risque de blocage que l'envoi réel juste en dessous, laissé en await direct.
    const orderId: bigint = await contract['placeSellOrder'].staticCall(orderParams);
    const owner = await signer.getAddress();

    let wcErr: any;
    contract['placeSellOrder'](orderParams)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => {
        const order = await this.getOrder(orderId.toString());
        return !!order && order.seller.toLowerCase() === owner.toLowerCase();
      },
      () => wcErr,
      "Délai dépassé — création de l'offre non confirmée à temps.",
    );
    return orderId.toString();
  }

  async rateTrade(signer: any, tradeId: string, rating: number): Promise<void> {
    const contract = this.writeExchange(signer);
    const owner = await signer.getAddress();

    let wcErr: any;
    contract['rateTrade'](tradeId, rating)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => this.hasRated(tradeId, owner),
      () => wcErr,
      'Délai dépassé — note non confirmée à temps.',
    );
  }

  async getUserRating(address: string): Promise<{ avgRating: string; totalRatings: string }> {
    try {
      const raw = await this._exchange.methods.getUserRating(address).call();
      return {
        avgRating:    String(raw.avgRating    ?? raw[0] ?? '0'),
        totalRatings: String(raw.totalRatings ?? raw[1] ?? '0'),
      };
    } catch { return { avgRating: '0', totalRatings: '0' }; }
  }

  async hasRated(tradeId: string, address: string): Promise<boolean> {
    try { return Boolean(await this._exchange.methods.hasRated(tradeId, address).call()); }
    catch { return false; }
  }

  formatRating(avgRating: string | number): string {
    const raw = Number(avgRating || '0');
    const normalized = raw > 5 ? raw / 100 : raw;
    return normalized > 0 ? normalized.toFixed(1) : '—';
  }

  /** Étoiles depuis les ratings explicites (rateTrade) — vides si 0 avis */
  ratingStars(rating: { avgRating: string; totalRatings: string } | null): boolean[] {
    if (!rating || Number(rating.totalRatings || '0') === 0) {
      return [false, false, false, false, false];
    }
    const raw        = Number(rating.avgRating || '0');
    const normalized = raw > 5 ? raw / 100 : raw;
    const score      = Math.max(0, Math.min(5, Math.round(normalized)));
    return [0, 1, 2, 3, 4].map(i => i < score);
  }

  /** Taux de réussite depuis la réputation */
  successRate(rep: P2PReputation | null): number {
    if (!rep) return 0;
    const total = Number(rep.total || '0');
    return total === 0 ? 0 : Math.round((Number(rep.success || '0') / total) * 100);
  }

  // ── Referral ──────────────────────────────────────────────────────────────

  async getReferrerOnChain(address: string): Promise<string> {
    try { return String(await this._exchange.methods.referrerOf(address).call()); }
    catch { return ''; }
  }

  async setReferrerOnChain(signer: any, referrerAddress: string): Promise<void> {
    const contract = this.writeExchange(signer);
    const owner = await signer.getAddress();

    let wcErr: any;
    contract['setReferrer'](referrerAddress)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getReferrerOnChain(owner)).toLowerCase() === referrerAddress.toLowerCase(),
      () => wcErr,
      'Délai dépassé — enregistrement du parrain non confirmé à temps.',
      60_000, // silencieux/best-effort (voir trySyncReferrer côté page) — pas la peine d'attendre 120s
    );
  }

  async fetchReferrerWallet(invitedByUserId: number): Promise<string | null> {
    try {
      const base = BASE_URL;
      const resp = await fetch(`${base}IrisUsers/${invitedByUserId}`);
      if (!resp.ok) return null;
      const user = await resp.json();
      return (user?.UserAddress as string) ?? null;
    } catch { return null; }
  }

  async claimRewards(signer: any): Promise<void> {
    const contract = this.writeExchange(signer);
    const owner = await signer.getAddress();
    const before = BigInt(await this.getUsdtBalance(owner));

    let wcErr: any;
    contract['claimRewards']()
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => BigInt(await this.getUsdtBalance(owner)) > before,
      () => wcErr,
      'Délai dépassé — claim des récompenses non confirmé à temps.',
    );
  }

  async claimReferralRewards(signer: any): Promise<void> {
    const contract = this.writeExchange(signer);
    const owner = await signer.getAddress();
    const before = BigInt(await this.getUsdtBalance(owner));

    let wcErr: any;
    contract['claimReferralRewards']()
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => BigInt(await this.getUsdtBalance(owner)) > before,
      () => wcErr,
      'Délai dépassé — claim des récompenses de parrainage non confirmé à temps.',
    );
  }

  // ── Arbitrage ─────────────────────────────────────────────────────────────

  async getJudge(address: string): Promise<JudgeInfo> {
    try {
      const raw: any = await this._exchange.methods.getJudge(address).call();
      return {
        active:        Boolean(raw.active        ?? raw[0] ?? false),
        balance:       String(raw.balance        ?? raw[2] ?? '0'),
        totalResolved: String(raw.totalResolved  ?? raw[3] ?? '0'),
      };
    } catch {
      return { active: false, balance: '0', totalResolved: '0' };
    }
  }

  async getOpenDisputes(): Promise<P2PTrade[]> {
    try {
      const count = Number(await this._exchange.methods.totalTrades().call().catch(() => 0));
      const limit  = Math.min(count, 200);
      const start  = Math.max(1, count - limit + 1);
      const ids    = Array.from({ length: count - start + 1 }, (_, i) => (count - i).toString());
      const results = await Promise.allSettled(ids.map(id => this.getTrade(id)));
      return results
        .filter((r): r is PromiseFulfilledResult<P2PTrade> => r.status === 'fulfilled' && !!r.value)
        .map(r => r.value)
        .filter(t => t.status === TradeStatus.Disputed);
    } catch { return []; }
  }

  // Litiges PASSÉS (résolus) uniquement — pour "en cours", voir getOpenDisputes() ci-dessus, qui
  // lit le STATUT COURANT des trades (fiable, pas d'events, jamais limité par une fenêtre de
  // temps) : un litige ouvert il y a longtemps mais toujours en cours doit rester visible même
  // hors de la fenêtre d'événements ci-dessous — les deux listes ne peuvent PAS partager la même
  // méthode. Base = événements TradeResolved (jamais supprimés on-chain), qui EST la seule preuve
  // qu'un litige a existé et a été tranché — TradeDisputed n'est utilisé qu'en enrichissement
  // optionnel (qui a initié), absent si son propre événement tombe hors fenêtre.
  //
  // BUG CORRIGÉ (2026-09-13, signalé par l'utilisateur : liste vide alors que des litiges
  // existaient) — voir EVENTS_RPC/EVENT_LOOKBACK_BLOCKS : BSC_RPC (bsc-dataseed.binance.org,
  // this._web3/_exchange) rejette eth_getLogs INCONDITIONNELLEMENT ("limit exceeded" vérifié
  // empiriquement, même sur 1 seul bloc) — cette méthode ne pouvait donc RIEN retourner depuis le
  // début. Fenêtre volontairement courte (~8h) — décision explicite de l'utilisateur plutôt que de
  // payer une clé API tierce pour couvrir toute la durée de vie du contrat (aucun fournisseur
  // gratuit testé — PublicNode, Ankr, NodeReal, BscScan/Etherscan v2 — ne le permet).
  async getPastDisputes(): Promise<DisputeRecord[]> {
    try {
      const currentBlock = Number(await this._eventsWeb3.eth.getBlockNumber().catch(() => 0));
      const fromBlock    = Math.max(0, currentBlock - EVENT_LOOKBACK_BLOCKS);
      const [disputed, resolved] = await Promise.all([
        this._eventsExchange.getPastEvents('TradeDisputed', { fromBlock, toBlock: 'latest' }).catch(() => []),
        this._eventsExchange.getPastEvents('TradeResolved',  { fromBlock, toBlock: 'latest' }).catch(() => []),
      ]);
      if ((resolved as any[]).length === 0) return [];

      const disputedByTradeId = new Map<string, any>();
      (disputed as any[]).forEach(e => disputedByTradeId.set(String(e.returnValues?.tradeId), e));

      // Timestamps de bloc — un seul appel par bloc DISTINCT plutôt qu'un par événement.
      const blockNumbers = new Set<number>([
        ...(disputed as any[]).map(e => e.blockNumber),
        ...(resolved as any[]).map(e => e.blockNumber),
      ]);
      const blocks = await Promise.all([...blockNumbers].map(bn => this._eventsWeb3.eth.getBlock(bn).catch(() => null)));
      const tsByBlock = new Map<number, number>();
      blocks.forEach((b: any) => { if (b) tsByBlock.set(Number(b.number), Number(b.timestamp) * 1000); });

      const records = await Promise.all((resolved as any[]).map(async e => {
        const tradeId = String(e.returnValues?.tradeId);
        const dis = disputedByTradeId.get(tradeId); // absent = TradeDisputed hors fenêtre, pas une erreur
        const trade = await this.getTrade(tradeId).catch(() => null);
        return {
          tradeId,
          initiator:  dis ? String(dis.returnValues?.initiator || '') : '',
          disputedAt: dis ? (tsByBlock.get(dis.blockNumber) ?? 0) : 0,
          resolved:   true,
          judge:      String(e.returnValues?.judge || ''),
          buyerWins:  Boolean(e.returnValues?.buyerWins),
          resolvedAt: tsByBlock.get(e.blockNumber),
          trade,
        } as DisputeRecord;
      }));

      return records.sort((a, b) => (b.resolvedAt ?? 0) - (a.resolvedAt ?? 0));
    } catch { return []; }
  }

  async resolveDispute(signer: any, tradeId: string, buyerWins: boolean): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['resolveDispute'](tradeId, buyerWins)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getTrade(tradeId))?.status !== TradeStatus.Disputed,
      () => wcErr,
      'Délai dépassé — résolution du litige non confirmée à temps.',
    );
  }

  async claimJudgeRewards(signer: any): Promise<void> {
    const contract = this.writeExchange(signer);
    const owner = await signer.getAddress();
    const before = BigInt(await this.getUsdtBalance(owner));

    let wcErr: any;
    contract['claimJudgeRewards']()
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => BigInt(await this.getUsdtBalance(owner)) > before,
      () => wcErr,
      'Délai dépassé — claim des récompenses juge non confirmé à temps.',
    );
  }

  // ── Admin — lecture ────────────────────────────────────────────────────────

  async getOwner(): Promise<string> {
    try { return String(await this._exchange.methods.owner().call()); }
    catch { return ''; }
  }

  async getAdminOverview(): Promise<AdminOverview> {
    const [owner, stats, balance, health, paused, pausedDep, pausedRew, pausedTax,
           maxSell, minSell, minFee, maxTrades, expTime, feePct, rewSplit, judgeTax] =
      await Promise.all([
        this._exchange.methods.owner().call(),
        this._exchange.methods.getContractStats().call().catch(() => null),
        this._exchange.methods.getContractBalance?.()?.call().catch(() => '0') ?? Promise.resolve('0'),
        this._exchange.methods.checkBalanceHealth?.()?.call().catch(() => null) ?? Promise.resolve(null),
        this._exchange.methods.paused().call().catch(() => false),
        this._exchange.methods.pausedDeposit().call().catch(() => false),
        this._exchange.methods.pausedRewards().call().catch(() => false),
        this._exchange.methods.pausedTaxes().call().catch(() => false),
        this._exchange.methods.maxSellableAmount().call().catch(() => '0'),
        this._exchange.methods.minSellableAmount().call().catch(() => '0'),
        this._exchange.methods.minFlatFee().call().catch(() => '0'),
        this._exchange.methods.maxActiveTrades().call().catch(() => '3'),
        this._exchange.methods.defaultExpirationTime().call().catch(() => '3600'),
        this._exchange.methods.feePercent().call().catch(() => '0'),
        this._exchange.methods.rewardSplit().call().catch(() => '5000'),
        this._exchange.methods.judgeTaxPct().call().catch(() => '0'),
      ]);
    return {
      owner:               String(owner),
      paused:              Boolean(paused),
      pausedDeposit:       Boolean(pausedDep),
      pausedRewards:       Boolean(pausedRew),
      pausedTaxes:         Boolean(pausedTax),
      totalOrders:         String(stats?.orders_    ?? stats?.[0] ?? '0'),
      totalTrades:         String(stats?.trades_    ?? stats?.[1] ?? '0'),
      totalSuccessfulTrades: String(stats?.successful_ ?? stats?.[2] ?? '0'),
      totalEscrowed:       String(stats?.escrowed_  ?? stats?.[3] ?? '0'),
      accumulatedFees:     String(stats?.fees_      ?? stats?.[4] ?? '0'),
      judgeCount:          String(stats?.judges_    ?? stats?.[5] ?? '0'),
      contractBalance:     String(balance ?? '0'),
      isBalanceHealthy:    Boolean(health?.isHealthy ?? health?.[0] ?? true),
      maxSellableAmount:   String(maxSell),
      minSellableAmount:   String(minSell),
      minFlatFee:          String(minFee),
      maxActiveTrades:     String(maxTrades),
      defaultExpirationTime: String(expTime),
      feePercent:          String(feePct),
      rewardSplit:         String(rewSplit),
      judgeTaxPct:         String(judgeTax),
    };
  }

  async getAllJudges(): Promise<JudgeRecord[]> {
    try {
      // BUG CORRIGÉ (2026-09-13) — voir EVENTS_RPC/EVENT_LOOKBACK_BLOCKS : BSC_RPC
      // (bsc-dataseed.binance.org) rejette eth_getLogs INCONDITIONNELLEMENT ("limit exceeded"),
      // cette méthode retournait donc [] à CHAQUE appel depuis toujours, silencieusement. Fenêtre
      // désormais réellement fonctionnelle mais volontairement courte (~8h, contrainte du
      // fournisseur gratuit) — un arbitre ajouté/retiré avant cette fenêtre n'apparaît plus ici.
      // Résidu connu, accepté par l'utilisateur (voir la même discussion pour getDisputeHistory).
      const currentBlock = Number(await this._eventsWeb3.eth.getBlockNumber().catch(() => 0));
      const fromBlock    = Math.max(0, currentBlock - EVENT_LOOKBACK_BLOCKS);
      const [added, removed] = await Promise.all([
        this._eventsExchange.getPastEvents('JudgeAdded',   { fromBlock, toBlock: 'latest' }).catch(() => []),
        this._eventsExchange.getPastEvents('JudgeRemoved', { fromBlock, toBlock: 'latest' }).catch(() => []),
      ]);
      const candidates = new Set<string>();
      [...added, ...removed].forEach((e: any) => {
        const addr = (e.returnValues?.judge || '').toLowerCase();
        if (addr) candidates.add(addr);
      });
      return Promise.all([...candidates].map(async address => {
        const info = await this.getJudge(address);
        const tgRaw = await this._exchange.methods.getTelegramUsername(address).call().catch(() => '');
        return { address, telegramUsername: this.fromBytes32String(String(tgRaw)), ...info };
      }));
    } catch { return []; }
  }

  // ── Admin — write ──────────────────────────────────────────────────────────

  // Lecture directe d'un getter public sans argument du contrat — utilisé UNIQUEMENT par les
  // polls admin ci-dessous (setters), pour éviter une méthode de lecture dédiée pour chacun.
  private async _readBool(method: string): Promise<boolean> {
    return Boolean(await this._exchange.methods[method]().call());
  }
  private async _readNum(method: string): Promise<bigint> {
    return BigInt(await this._exchange.methods[method]().call());
  }
  private async _isBanned(address: string): Promise<boolean> {
    const r: any = await this._exchange.methods.getAccount(address).call();
    return Boolean(r.banned ?? r[1] ?? false);
  }

  async addJudge(signer: any, judgeAddress: string, telegram: string): Promise<void> {
    const contract = this.writeExchange(signer);
    const tgBytes  = this.strToBytes32Pub(telegram.replace(/^@/, '').trim());

    let wcErr: any;
    contract['addJudge'](judgeAddress, tgBytes)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getJudge(judgeAddress)).active,
      () => wcErr,
      'Délai dépassé — ajout du juge non confirmé à temps.',
    );
  }

  async removeJudge(signer: any, judgeAddress: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['removeJudge'](judgeAddress)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => !(await this.getJudge(judgeAddress)).active,
      () => wcErr,
      'Délai dépassé — retrait du juge non confirmé à temps.',
    );
  }

  async banUser(signer: any, userAddress: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['banUser'](userAddress)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => this._isBanned(userAddress),
      () => wcErr,
      'Délai dépassé — bannissement non confirmé à temps.',
    );
  }

  async unbanUser(signer: any, userAddress: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['unbanUser'](userAddress)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => !(await this._isBanned(userAddress)),
      () => wcErr,
      'Délai dépassé — débannissement non confirmé à temps.',
    );
  }

  async adminPause(signer: any): Promise<void> {
    let wcErr: any;
    this.writeExchange(signer)['pause']()
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => this._readBool('paused'),
      () => wcErr,
      'Délai dépassé — mise en pause non confirmée à temps.',
    );
  }

  async adminUnpause(signer: any): Promise<void> {
    let wcErr: any;
    this.writeExchange(signer)['unpause']()
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => !(await this._readBool('paused')),
      () => wcErr,
      'Délai dépassé — reprise non confirmée à temps.',
    );
  }

  async setPausedDeposit(signer: any, paused: boolean): Promise<void> {
    let wcErr: any;
    this.writeExchange(signer)['setPausedDeposit'](paused)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readBool('pausedDeposit')) === paused,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setPausedRewards(signer: any, paused: boolean): Promise<void> {
    let wcErr: any;
    this.writeExchange(signer)['setPausedRewards'](paused)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readBool('pausedRewards')) === paused,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setMaxSellableAmount(signer: any, amountHuman: string): Promise<void> {
    const target = BigInt(this.toWei(amountHuman));
    let wcErr: any;
    this.writeExchange(signer)['setMaxSellableAmount'](target)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('maxSellableAmount')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setMinSellableAmount(signer: any, amountHuman: string): Promise<void> {
    const target = BigInt(this.toWei(amountHuman));
    let wcErr: any;
    this.writeExchange(signer)['setMinSellableAmount'](target)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('minSellableAmount')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setMaxActiveTrades(signer: any, max: string): Promise<void> {
    const target = BigInt(max);
    let wcErr: any;
    this.writeExchange(signer)['setMaxActiveTrades'](Number(max))
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('maxActiveTrades')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setFeePercent(signer: any, bps: string): Promise<void> {
    const target = BigInt(bps);
    let wcErr: any;
    this.writeExchange(signer)['setFeePercent'](Number(bps))
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('feePercent')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setMinFlatFee(signer: any, amountHuman: string): Promise<void> {
    const target = BigInt(this.toWei(amountHuman));
    let wcErr: any;
    this.writeExchange(signer)['setMinFlatFee'](target)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('minFlatFee')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setJudgeTax(signer: any, bps: string): Promise<void> {
    const target = BigInt(bps);
    let wcErr: any;
    this.writeExchange(signer)['setJudgeTax'](Number(bps))
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('judgeTaxPct')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async setDefaultExpirationTime(signer: any, seconds: string): Promise<void> {
    const target = BigInt(seconds);
    let wcErr: any;
    this.writeExchange(signer)['setDefaultExpirationTime'](Number(seconds))
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this._readNum('defaultExpirationTime')) === target,
      () => wcErr,
      'Délai dépassé — changement non confirmé à temps.',
    );
  }

  async withdrawFees(signer: any, toAddress: string, amountHuman: string): Promise<void> {
    const before = BigInt(await this.getUsdtBalance(toAddress));
    const expectedIncrease = BigInt(this.toWei(amountHuman));

    let wcErr: any;
    this.writeExchange(signer)['withdrawFees'](toAddress, expectedIncrease)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => BigInt(await this.getUsdtBalance(toAddress)) > before,
      () => wcErr,
      'Délai dépassé — retrait des frais non confirmé à temps.',
    );
  }

  // Expose strToBytes32 pour les usages admin
  strToBytes32Pub(value: string): string { return this.strToBytes32(value); }
  fromBytes32String(hex: string): string  { return this.fromBytes32(hex); }

  async cancelSellOrder(signer: any, orderId: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['cancelSellOrder'](orderId)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getOrder(orderId))?.status === OrderStatus.Cancelled,
      () => wcErr,
      "Délai dépassé — annulation de l'offre non confirmée à temps.",
    );
  }

  async updateOrderRate(signer: any, orderId: string, rateUnits: string): Promise<void> {
    const contract = this.writeExchange(signer);
    const target = BigInt(rateUnits);

    let wcErr: any;
    contract['updateOrderRate'](orderId, target)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => BigInt((await this.getOrder(orderId))?.rate ?? '0') === target,
      () => wcErr,
      'Délai dépassé — changement de taux non confirmé à temps.',
    );
  }

  // ── Write — Buy flow ──────────────────────────────────────────────────────

  async requestTrade(signer: any, orderId: string, amountRaw: string): Promise<string> {
    const contract = this.writeExchange(signer);
    const owner = await signer.getAddress();

    // staticCall = simulation en lecture seule (eth_call, pas de signature) — donne le futur
    // tradeId sans attendre l'envoi réel, même principe que placeSellOrder() plus haut. Remplace
    // l'ancien parsing du log TradeRequested après coup (qui exigeait le receipt de la tx réelle,
    // donc un await direct sur la signature WC pour l'obtenir).
    const tradeId: bigint = await contract['requestTrade'].staticCall(orderId, BigInt(amountRaw));

    let wcErr: any;
    contract['requestTrade'](orderId, BigInt(amountRaw))
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => {
        const trade = await this.getTrade(tradeId.toString());
        return !!trade && trade.buyer.toLowerCase() === owner.toLowerCase();
      },
      () => wcErr,
      "Délai dépassé — demande de trade non confirmée à temps.",
    );
    return tradeId.toString();
  }

  async markPaid(signer: any, tradeId: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['markPaid'](tradeId)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getTrade(tradeId))?.status === TradeStatus.Paid,
      () => wcErr,
      'Délai dépassé — marquage "payé" non confirmé à temps.',
    );
  }

  async executePurchase(signer: any, tradeId: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['executePurchase'](tradeId)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getTrade(tradeId))?.status === TradeStatus.Completed,
      () => wcErr,
      'Délai dépassé — libération des fonds non confirmée à temps.',
    );
  }

  async reclaimExpired(signer: any, tradeId: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['reclaimExpired'](tradeId)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      // Un trade expiré réclamé n'est plus Open (voir getTrade/canReclaim plus haut) — Cancelled
      // est l'état attendu, mais on accepte tout changement hors Open pour rester robuste si le
      // contrat le range ailleurs.
      async () => (await this.getTrade(tradeId))?.status !== TradeStatus.Open,
      () => wcErr,
      'Délai dépassé — réclamation non confirmée à temps.',
    );
  }

  async raiseDispute(signer: any, tradeId: string): Promise<void> {
    const contract = this.writeExchange(signer);

    let wcErr: any;
    contract['raiseDispute'](tradeId)
      .then((tx: any) => this._directWait(tx))
      .catch((e: any) => { wcErr = e; });

    await this._pollUntil(
      async () => (await this.getTrade(tradeId))?.status === TradeStatus.Disputed,
      () => wcErr,
      'Délai dépassé — ouverture du litige non confirmée à temps.',
    );
  }

  // ── Encoding / Decoding (matching IrisP2PV3Service exactly) ───────────────

  /** rate × 1e8 → string (e.g. 0.93 → '93000000') */
  toRateUnits(rate: string | number): string {
    return (BigInt(Math.round(Number(rate) * 1e8))).toString();
  }

  /** raw rate units → human string (e.g. '93000000' → '0.93') */
  fromRateUnits(rate: string | number): string {
    const raw       = BigInt(rate || '0');
    const whole     = raw / BigInt(100_000_000);
    const remainder = raw % BigInt(100_000_000);
    const dec = remainder.toString().padStart(8, '0').slice(0, 4).replace(/0+$/, '');
    return dec.length > 0 ? `${this.sep(whole)}.${dec}` : this.sep(whole);
  }

  /** USDT amount in wei → number (pour calculs, sans formatage) */
  fromWeiNum(raw: string | bigint, precision = 2): number {
    try {
      const wei  = BigInt(raw || '0');
      const unit = BigInt(10) ** BigInt(TOKEN_DECIMALS);
      const whole = Number(wei / unit);
      const rem   = Number(wei % unit);
      return +(whole + rem / Math.pow(10, TOKEN_DECIMALS)).toFixed(precision);
    } catch { return 0; }
  }

  /** USDT amount in wei → human string */
  fromWei(raw: string | bigint, precision = 2): string {
    try {
      const wei  = BigInt(raw || '0');
      const unit = BigInt(10) ** BigInt(TOKEN_DECIMALS);
      const whole = wei / unit;
      const rem   = wei % unit;
      const dec = rem.toString().padStart(TOKEN_DECIMALS, '0').slice(0, precision).replace(/0+$/, '');
      return dec.length > 0 ? `${this.sep(whole)}.${dec}` : this.sep(whole);
    } catch { return '0'; }
  }

  /** Exact fiat total for a USDT wei amount at the given rate units */
  fiatAmountFromWei(amountRaw: string | bigint, rateUnits: string | bigint, currency = '', precision?: number): string {
    try {
      const amount = BigInt(amountRaw || '0');
      const rate = BigInt(rateUnits || '0');
      const scale = BigInt(10) ** BigInt(TOKEN_DECIMALS + 8);
      const total = amount * rate;
      const whole = total / scale;
      const rem = total % scale;
      const decimals = precision ?? (['XAF', 'JPY', 'KRW', 'VND'].includes((currency || '').toUpperCase()) ? 0 : 2);
      if (decimals <= 0) return this.sep(whole);
      const dec = rem.toString().padStart(Number(TOKEN_DECIMALS + 8), '0').slice(0, decimals).replace(/0+$/, '');
      return dec.length > 0 ? `${this.sep(whole)}.${dec}` : this.sep(whole);
    } catch { return '0'; }
  }

  /** Human USDT amount → wei string */
  toWei(amount: string | number): string {
    const clean = `${amount}`.trim();
    const [wholePart, decimalPart = ''] = clean.split('.');
    const whole = BigInt(wholePart || '0');
    const dec   = decimalPart.padEnd(TOKEN_DECIMALS, '0').slice(0, TOKEN_DECIMALS);
    return (whole * BigInt(10) ** BigInt(TOKEN_DECIMALS) + BigInt(dec || '0')).toString();
  }

  /** Estimated net escrow after fee */
  estimatedFee(amountRaw: string, feePercent: string, minFlatFee: string): bigint {
    const amt        = BigInt(amountRaw || '0');
    const percentFee = amt * BigInt(feePercent || '0') / BigInt(10_000);
    const flatFee    = BigInt(minFlatFee || '0');
    return percentFee > flatFee ? percentFee : flatFee;
  }

  netEscrow(amountRaw: string, feePercent: string, minFlatFee: string): bigint {
    const net = BigInt(amountRaw || '0') - this.estimatedFee(amountRaw, feePercent, minFlatFee);
    return net > BigInt(0) ? net : BigInt(0);
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  paymentLabel(method: string): string {
    return PAYMENT_LABELS[method] ?? method.replace(/_/g, ' ');
  }

  paymentLabels(methods: string[]): string {
    const shown = methods.slice(0, 3).map(m => this.paymentLabel(m)).join(', ');
    return methods.length > 3 ? `${shown} +${methods.length - 3}` : shown;
  }


  shortAddress(addr: string): string {
    if (!addr || addr.length < 10) return addr;
    return addr.slice(0, 6) + '…' + addr.slice(-4);
  }

  timeUntilExpiry(expiresAt: number): string {
    const diff = Math.floor((expiresAt - Date.now()) / 1000);
    if (diff <= 0) return 'Expiré';
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  isSessionError(err: any): boolean {
    const msg  = (err?.message ?? '').toLowerCase();
    const code = err?.code ?? err?.data?.code ?? 0;
    return msg === 'no_provider'
      || code === 6000
      || (msg.includes('session') && !msg.includes('gas'))
      || msg.includes('disconnected')
      || msg.includes('expired')
      // wc_sign_timeout (2026-08-24) — le relay WC n'a jamais délivré la réponse à la demande de
      // signature (voir capture /p2p/p2p-sell) : c'est un hoquet de SESSION, pas une vraie erreur
      // de transaction — doit déclencher le flux de reconnexion, pas un message d'erreur générique.
      || msg.includes('wc_sign_timeout');
  }

  errorMessage(err: any): string {
    if (this.isSessionError(err))
      return 'Session wallet expirée — reconnectez votre wallet pour signer.';
    const text = [err?.reason, err?.data?.message, err?.error?.message, err?.message, `${err || ''}`]
      .filter(Boolean).join(' ');
    if (text.includes('Set Telegram username first')) return 'Définis ton username Telegram avant de trader.';
    if (text.includes('Below minimum'))               return "Montant inférieur au minimum de l'offre.";
    if (text.includes('Exceeds maximum'))             return "Montant supérieur au maximum de l'offre.";
    if (text.includes('Insufficient available'))      return "Liquidité insuffisante.";
    if (text.includes('Cannot buy own order'))        return "Tu ne peux pas acheter ta propre offre.";
    if (text.includes('Already in queue'))            return "Tu as déjà une demande sur cette offre.";
    if (text.includes('Too many active trades'))      return "Trop de trades actifs.";
    if (text.includes('Queue full'))                  return "File d'attente pleine.";
    if (text.includes('Order not active'))            return "Cette offre n'est plus active.";
    if (text.includes('Trades in progress'))          return "Impossible pour l'instant : un trade est en cours sur cette offre (reçois/réclame-le d'abord).";
    if (text.includes('Rate must be positive'))       return "Le taux doit être supérieur à 0.";
    if (text.includes('User denied') || text.includes('rejected')) return 'Transaction annulée dans le wallet.';
    // Le trade P2P se crée sur BSC : l'acheteur paie le gas en BNB. Sans BNB, la transaction
    // échoue à l'estimation/envoi ("insufficient funds for intrinsic transaction cost").
    if (/insufficient funds|intrinsic transaction cost|exceeds the balance/i.test(text))
      return "Il te faut un petit peu de BNB sur BSC pour payer les frais réseau du trade.";

    // BUG CORRIGÉ (2026-08-24) : ce repli renvoyait err.message TEL QUEL, sans garde — pour une
    // erreur RPC non classifiée (ex: "could not coalesce error(error={...}, payload={...}")), c'est
    // le JSON complet de la requête ethers.js qui s'affichait brut à l'utilisateur (voir capture
    // "wc_sign_timeout" sur /p2p/p2p-sell). Même garde que cleanTxError() dans buy-iris.page.ts/
    // token-swap.page.ts : un message qui ressemble à du JSON/debug interne devient un message
    // générique lisible plutôt que d'être affiché tel quel.
    const raw = err?.reason || err?.message || `${err}`;
    const looksLikeJunk = !raw || raw.length > 150 || raw.includes('coalesce') || /^[{[]/.test(raw.trim()) || /"code"\s*:/.test(raw);
    return looksLikeJunk ? 'Erreur de connexion au réseau — réessaie dans quelques instants.' : raw;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private sep(n: bigint | number): string {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  // ── Private encoding (matching IrisP2PV3Service) ──────────────────────────

  private strToBytes3(value: string): string {
    const clean = (value || '').trim().toUpperCase().slice(0, 3).padEnd(3, ' ');
    const hex = Array.from(clean).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    return '0x' + hex.padEnd(6, '0');
  }

  private strToBytes32(value: string): string {
    const clean = (value || '').trim().slice(0, 31);
    const hex = Array.from(clean).map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    return '0x' + hex.padEnd(64, '0');
  }

  private fromBytes32(hex: string): string {
    if (!hex) return '';
    const cleanHex = hex.replace(/^0x/, '');
    let result = '';
    for (let i = 0; i < cleanHex.length; i += 2) {
      const code = parseInt(cleanHex.slice(i, i + 2), 16);
      if (code === 0) break;
      result += String.fromCharCode(code);
    }
    return result.trim();
  }

  private fromBytes3(hex: string): string {
    return this.fromBytes32(hex);
  }
}

const PAYMENT_LABELS: Record<string, string> = {
  BANK_TRANSFER: 'Virement bancaire', BANK_CARD: 'Carte bancaire', SEPA: 'SEPA', SWIFT: 'SWIFT',
  WISE: 'Wise', REVOLUT: 'Revolut', PAYPAL: 'PayPal', SKRILL: 'Skrill', NETELLER: 'Neteller',
  PAYEER: 'Payeer', ADVCASH: 'Advcash', PERFECT_MONEY: 'Perfect Money',
  ZELLE: 'Zelle', VENMO: 'Venmo', CASH_APP: 'Cash App', CHIME: 'Chime',
  APPLE_PAY: 'Apple Pay', GOOGLE_PAY: 'Google Pay', AIRTM: 'AirTM', ZINLI: 'Zinli',
  PIX: 'Pix', TED: 'TED', SPEI: 'SPEI', OXXO: 'OXXO', MERCADO_PAGO: 'Mercado Pago',
  UPI: 'UPI', IMPS: 'IMPS', NEFT: 'NEFT', PAYTM: 'Paytm', PHONEPE: 'PhonePe',
  ALIPAY: 'Alipay', WECHAT_PAY: 'WeChat Pay', QQ_PAY: 'QQ Pay',
  GCASH: 'GCash', MAYA: 'Maya', SHOPEEPAY: 'ShopeePay', DANA: 'DANA', OVO: 'OVO',
  GOPAY: 'GoPay', LINKAJA: 'LinkAja', MOMO: 'MoMo', ZALO_PAY: 'ZaloPay', VIETTELPAY: 'ViettelPay',
  MOBILE_MONEY: 'Mobile Money', MTN_MOMO: 'MTN MoMo', ORANGE_MONEY: 'Orange Money',
  AIRTEL_MONEY: 'Airtel Money', MOOV_MONEY: 'Moov Money', MPESA: 'M-Pesa',
  WAVE: 'Wave', FREE_MONEY: 'Free Money',
  CASH_DEPOSIT: 'Dépôt espèces', CASH_IN_PERSON: 'Espèces en main propre', CASH: 'Espèces',
  WESTERN_UNION: 'Western Union', MONEYGRAM: 'MoneyGram', RIA: 'RIA',
  WORLDREMIT: 'WorldRemit', REMITLY: 'Remitly',
};
